/**
 * 用户管理页面 - 支持新的用户认证体系
 * 
 * 功能：
 * 1. 显示当前用户信息（包括认证状态）
 * 2. 账号切换（本地账号列表）
 * 3. 添加账号（登录/注册/找回）
 * 4. 绑定手机号、设置密码等
 */
import React, { useState, useEffect } from 'react';
import { useDataStore } from '../store/useDataStore';
import { 
  getAllUsers, 
  addOrUpdateUser,
  updateUserNickname,
  type UserInfo
} from '../services/userManager';
import { 
  verifyUser, 
  getCurrentUserInfo,
  bindPhone,
  setPassword,
  changePassword,
  sendSmsCode
} from '../services/api';
import { 
  User, Copy, Check, Plus, Edit2, Save, X, RefreshCw, Info, AlertTriangle,
  Phone, Lock, Shield, LogOut, Settings, Key, Smartphone, Eye, EyeOff, Loader2
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import AuthDialog from './UI/AuthDialog';

// 确认弹窗组件
interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  targetUser?: UserInfo | null;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ 
  isOpen, 
  title, 
  message, 
  targetUser,
  onConfirm, 
  onCancel,
  isLoading 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      
      <div className="relative bg-white rounded-[32px] p-8 max-w-md w-full mx-4 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
        </div>
        
        <h3 className="text-xl font-black text-gray-800 text-center mb-3">
          {title}
        </h3>
        
        <p className="text-gray-500 text-center mb-4">
          {message}
        </p>
        
        {targetUser && (
          <div className="bg-gray-50 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-red-400 flex items-center justify-center text-white font-bold">
                {(targetUser.nickname || 'A')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-700 truncate">
                  {targetUser.nickname || '未命名用户'}
                </div>
                <div className="text-sm text-gray-600 font-mono font-semibold truncate">
                  {targetUser.userUuid}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-all disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-[#ff5c5c] hover:bg-[#ff4040] text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                切换中...
              </>
            ) : (
              '确认切换'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// 绑定手机号弹窗
interface BindPhoneDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  token: string;
}

const BindPhoneDialog: React.FC<BindPhoneDialogProps> = ({ isOpen, onClose, onSuccess, token }) => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入正确的手机号');
      return;
    }
    setIsLoading(true);
    setError(null);
    const result = await sendSmsCode({ phone, type: 'bind_phone' });
    setIsLoading(false);
    if (result.success) {
      setCountdown(60);
      setSuccess(result.data?.code ? `验证码: ${result.data.code}` : '验证码已发送');
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.message);
    }
  };

  const handleBind = async () => {
    if (!phone || !code) {
      setError('请填写手机号和验证码');
      return;
    }
    setIsLoading(true);
    setError(null);
    const result = await bindPhone({ phone, code }, token);
    setIsLoading(false);
    if (result.success) {
      onSuccess();
      onClose();
    } else {
      setError(result.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-[32px] p-8 max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-xl font-black text-gray-800 mb-6">绑定手机号</h3>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-xl text-green-600 text-sm">
            {success}
          </div>
        )}

        <div className="space-y-4">
          <div className="relative">
            <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="手机号"
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 outline-none"
            />
          </div>

          <div className="flex gap-3">
            <div className="relative flex-1">
              <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="验证码"
                maxLength={6}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 outline-none"
              />
            </div>
            <button
              onClick={handleSendCode}
              disabled={countdown > 0 || isLoading}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium disabled:opacity-50"
            >
              {countdown > 0 ? `${countdown}s` : '获取验证码'}
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold">
              取消
            </button>
            <button
              onClick={handleBind}
              disabled={isLoading}
              className="flex-1 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 size={18} className="animate-spin" />}
              绑定
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 设置/修改密码弹窗
interface PasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  token: string;
  hasPassword: boolean;
}

const PasswordDialog: React.FC<PasswordDialogProps> = ({ isOpen, onClose, onSuccess, token, hasPassword }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (hasPassword && !oldPassword) {
      setError('请输入原密码');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError('新密码至少6位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);
    setError(null);

    let result;
    if (hasPassword) {
      result = await changePassword({ oldPassword, newPassword }, token);
    } else {
      result = await setPassword({ newPassword }, token);
    }

    setIsLoading(false);
    if (result.success) {
      onSuccess();
      onClose();
    } else {
      setError(result.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-[32px] p-8 max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-xl font-black text-gray-800 mb-6">
          {hasPassword ? '修改密码' : '设置密码'}
        </h3>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {hasPassword && (
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="原密码"
                className="w-full pl-12 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 outline-none"
              />
            </div>
          )}

          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="新密码（至少6位）"
              className="w-full pl-12 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="确认新密码"
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 outline-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold">
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex-1 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 size={18} className="animate-spin" />}
              {hasPassword ? '修改' : '设置'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UserView: React.FC = () => {
  const { userUuid, setUserUuid, authToken, setAuthToken } = useDataStore();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNickname, setEditingNickname] = useState<string | null>(null);
  const [nicknameInput, setNicknameInput] = useState('');
  
  // 服务器用户信息
  const [serverUserInfo, setServerUserInfo] = useState<{
    username?: string;
    phone?: string;
    role?: string;
    hasPassword?: boolean;
    email?: string;
    nickname?: string;
  } | null>(null);
  
  // 弹窗状态
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSwitchUser, setPendingSwitchUser] = useState<UserInfo | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<'login' | 'register' | 'sms-login' | 'find-uuid' | 'add-existing'>('login');
  const [showBindPhoneDialog, setShowBindPhoneDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  useEffect(() => {
    loadUsers();
    if (userUuid) {
      syncUserInfo();
    }
  }, [userUuid]);

  const syncUserInfo = async () => {
    if (!userUuid) return;
    try {
      // 从服务器同步用户信息
      const { syncUserInfoFromServer } = await import('../services/userManager');
      await syncUserInfoFromServer(userUuid);
      loadUsers();
      
      // 如果有 token，获取完整用户信息
      if (authToken) {
        const result = await getCurrentUserInfo(authToken);
        if (result.success && result.data) {
          setServerUserInfo({
            username: result.data.username,
            phone: result.data.phone,
            role: result.data.role,
            hasPassword: result.data.hasPassword,
            email: result.data.email,
            nickname: result.data.nickname
          });
        }
      }
    } catch (error) {
      console.warn('同步用户信息失败:', error);
    }
  };

  const loadUsers = async () => {
    const allUsers = await getAllUsers();
    allUsers.sort((a, b) => b.lastLoginTime - a.lastLoginTime);
    setUsers(allUsers);
  };

  const currentUser = users.find(u => u.userUuid === userUuid);

  const handleCopyUuid = (uuid: string) => {
    navigator.clipboard.writeText(uuid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSwitchClick = (targetUuid: string) => {
    if (targetUuid === userUuid) return;
    const targetUser = users.find(u => u.userUuid === targetUuid);
    setPendingSwitchUser(targetUser || null);
    setShowConfirmDialog(true);
  };

  const handleConfirmSwitch = async () => {
    if (!pendingSwitchUser) return;
    
    const targetUuid = pendingSwitchUser.userUuid;
    
    try {
      setIsSwitching(true);
      setError(null);
      
      await setUserUuid(targetUuid);
      // 清除旧的认证信息
      setAuthToken(null, null);
      setServerUserInfo(null);
      loadUsers();
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setShowConfirmDialog(false);
      setPendingSwitchUser(null);
      
      window.dispatchEvent(new CustomEvent('userSwitched', { detail: { userUuid: targetUuid } }));
      
      const win = window as any;
      if (typeof win.loadProject === 'function') {
        const { data: newData } = useDataStore.getState();
        if (newData.projects && newData.projects.length > 0) {
          await win.loadProject(newData.projects[0].uuid || newData.projects[0].id);
        }
      }
      
      console.log('[UserView] 用户切换完成:', targetUuid);
    } catch (err: any) {
      setError('切换用户失败: ' + err.message);
    } finally {
      setIsSwitching(false);
    }
  };

  const handleCancelSwitch = () => {
    setShowConfirmDialog(false);
    setPendingSwitchUser(null);
  };

  const handleSwitch = async (targetUuid: string) => {
    if (targetUuid === userUuid) return;
    try {
      setError(null);
      await setUserUuid(targetUuid);
      loadUsers();
      await new Promise(resolve => setTimeout(resolve, 100));
      window.dispatchEvent(new CustomEvent('userSwitched', { detail: { userUuid: targetUuid } }));
      
      const win = window as any;
      if (typeof win.loadProject === 'function') {
        const { data: newData } = useDataStore.getState();
        if (newData.projects && newData.projects.length > 0) {
          await win.loadProject(newData.projects[0].uuid || newData.projects[0].id);
        }
      }
    } catch (err: any) {
      setError('切换用户失败: ' + err.message);
    }
  };

  // 认证成功回调
  const handleAuthSuccess = async (result: {
    userUuid: string;
    username?: string;
    nickname?: string;
    token?: string;
    refreshToken?: string;
    isNewUser?: boolean;
  }) => {
    try {
      // 保存认证信息
      if (result.token && result.refreshToken) {
        setAuthToken(result.token, result.refreshToken);
      }
      
      // 添加到本地用户列表
      await addOrUpdateUser(result.userUuid, result.nickname);
      
      // 如果是新用户或不同用户，创建本地数据库
      if (result.userUuid !== userUuid) {
        const { initUserDB } = await import('../services/dbManager');
        await initUserDB(result.userUuid);
      }
      
      // 切换到该用户
      await handleSwitch(result.userUuid);
      
      // 更新服务器用户信息
      if (result.token) {
        const userInfo = await getCurrentUserInfo(result.token);
        if (userInfo.success && userInfo.data) {
          setServerUserInfo({
            username: userInfo.data.username,
            phone: userInfo.data.phone,
            role: userInfo.data.role,
            hasPassword: userInfo.data.hasPassword,
            email: userInfo.data.email,
            nickname: userInfo.data.nickname
          });
        }
      }
      
      setShowAuthDialog(false);
    } catch (err: any) {
      setError('添加账号失败: ' + err.message);
    }
  };

  const handleCreateNew = async () => {
    try {
      const newUuid = uuidv4();
      await addOrUpdateUser(newUuid);
      await handleSwitch(newUuid);
    } catch (err: any) {
      setError('创建新用户失败: ' + err.message);
    }
  };

  const handleEditNickname = () => {
    setEditingNickname(userUuid || '');
    setNicknameInput(currentUser?.nickname || '');
  };

  const handleSaveNickname = async () => {
    if (!userUuid) return;
    try {
      await updateUserNickname(userUuid, nicknameInput.trim());
      setEditingNickname(null);
      setNicknameInput('');
      loadUsers();
    } catch (error: any) {
      setError('保存昵称失败: ' + (error.message || '未知错误'));
    }
  };

  const handleCancelEdit = () => {
    setEditingNickname(null);
    setNicknameInput('');
  };

  // 退出登录（清除认证信息，但保留本地数据）
  const handleLogout = () => {
    setAuthToken(null, null);
    setServerUserInfo(null);
  };

  // 获取角色显示名称
  const getRoleDisplay = (role?: string) => {
    switch (role) {
      case 'super_admin': return '超级管理员';
      case 'admin': return '管理员';
      case 'user': return '普通用户';
      default: return '未知';
    }
  };

  // 获取角色颜色
  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto h-full overflow-y-auto custom-scrollbar">
      <div className="mb-10">
        <h1 className="text-3xl font-black text-gray-800 mb-2 tracking-tight">我的</h1>
        <p className="text-gray-400 font-medium">👤 管理你的账号和个人信息</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-medium flex items-center gap-2">
          <Info size={16} />
          {error}
        </div>
      )}

      {/* 当前用户信息卡片 */}
      {currentUser && (
        <div className="bg-white rounded-[40px] p-8 shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-pink-50 mb-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-pink-50 rounded-bl-[100px] -z-10" />
          
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-24 h-24 rounded-[32px] bg-gradient-to-br from-pink-400 to-red-400 flex items-center justify-center text-white shadow-lg shadow-pink-200">
              <User size={48} strokeWidth={2.5} />
            </div>
            
            <div className="flex-1 text-center md:text-left w-full">
              <div className="mb-4">
                {editingNickname === userUuid ? (
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <input
                      type="text"
                      value={nicknameInput}
                      onChange={(e) => setNicknameInput(e.target.value)}
                      placeholder="输入昵称"
                      className="px-4 py-2 border-2 border-pink-200 rounded-xl focus:border-[#ff5c5c] focus:ring-4 focus:ring-pink-100 outline-none transition-all text-lg font-bold"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveNickname();
                        else if (e.key === 'Escape') handleCancelEdit();
                      }}
                    />
                    <button onClick={handleSaveNickname} className="p-2 bg-green-500 text-white rounded-xl hover:bg-green-600" title="保存">
                      <Save size={20} />
                    </button>
                    <button onClick={handleCancelEdit} className="p-2 bg-gray-400 text-white rounded-xl hover:bg-gray-500" title="取消">
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                    <h2 className="text-2xl font-black text-gray-800">
                      {serverUserInfo?.nickname || currentUser.nickname || '未命名用户'}
                    </h2>
                    <button onClick={handleEditNickname} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-[#ff5c5c]" title="编辑昵称">
                      <Edit2 size={18} />
                    </button>
                    {serverUserInfo?.role && (
                      <span className={`px-2 py-1 rounded-lg text-xs font-bold ${getRoleColor(serverUserInfo.role)}`}>
                        {getRoleDisplay(serverUserInfo.role)}
                      </span>
                    )}
                  </div>
                )}
                
                {/* 用户名显示 - 更明显的样式 */}
                <div className="mb-3">
                  {authToken ? (
                    serverUserInfo?.username ? (
                      <div className="flex items-center gap-2 justify-center md:justify-start">
                        <span className="text-xs font-medium text-gray-500">登录账号：</span>
                        <span className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm font-semibold text-blue-700">
                          @{serverUserInfo.username}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-center md:justify-start text-sm text-gray-400">
                        <span>登录账号：</span>
                        <span className="text-orange-500">未设置用户名</span>
                        <span className="text-xs text-gray-400">（注册时设置）</span>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-2 justify-center md:justify-start text-sm text-gray-400">
                      <span>登录账号：</span>
                      <span className="text-blue-500">请先登录以查看用户名</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
                <code className="bg-gray-100 px-4 py-2 rounded-lg text-base font-mono break-all text-gray-700 font-semibold">
                  {userUuid}
                </code>
                <button 
                  onClick={() => handleCopyUuid(userUuid || '')}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-blue-500"
                  title="复制UUID"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>

              {/* 账号安全信息 */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-4">
                {serverUserInfo?.phone ? (
                  <div className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">
                    <Phone size={14} />
                    <span>{serverUserInfo.phone}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowBindPhoneDialog(true)}
                    disabled={!authToken}
                    className="flex items-center gap-1.5 text-sm text-orange-600 bg-orange-50 px-3 py-1.5 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50"
                  >
                    <Phone size={14} />
                    <span>绑定手机号</span>
                  </button>
                )}
                
                {serverUserInfo?.hasPassword ? (
                  <button
                    onClick={() => setShowPasswordDialog(true)}
                    disabled={!authToken}
                    className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                  >
                    <Lock size={14} />
                    <span>修改密码</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowPasswordDialog(true)}
                    disabled={!authToken}
                    className="flex items-center gap-1.5 text-sm text-orange-600 bg-orange-50 px-3 py-1.5 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50"
                  >
                    <Key size={14} />
                    <span>设置密码</span>
                  </button>
                )}
              </div>

              {/* 认证状态 */}
              {authToken ? (
                <div className="flex items-center justify-center md:justify-start gap-3">
                  <div className="flex items-center gap-1.5 text-sm text-green-600">
                    <Shield size={14} />
                    <span>已认证</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors"
                  >
                    <LogOut size={14} />
                    <span>退出登录</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setAuthDialogMode('login');
                    setShowAuthDialog(true);
                  }}
                  className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <Shield size={14} />
                  <span>登录以启用更多功能</span>
                </button>
              )}

              <div className="text-sm text-gray-500 mt-4">
                <div>创建时间: {new Date(currentUser.createdAt).toLocaleString('zh-CN')}</div>
                <div>最后登录: {new Date(currentUser.lastLoginTime).toLocaleString('zh-CN')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 账号切换 */}
      <div className="mb-10">
        <h3 className="text-xl font-black text-gray-800 mb-6 px-2">账号切换</h3>
        <div className="space-y-3">
          {users.map(user => (
            <div 
              key={user.userUuid}
              className={`p-5 rounded-[28px] border transition-all flex items-center justify-between ${
                user.userUuid === userUuid 
                  ? 'bg-pink-50/30 border-[#ff5c5c]/20 ring-2 ring-[#ff5c5c]/5' 
                  : 'bg-white border-gray-100 hover:border-pink-100'
              }`}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold flex-shrink-0 ${
                  user.userUuid === userUuid ? 'bg-[#ff5c5c] text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                  {(user.nickname || 'A')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-700 truncate">{user.nickname || '未命名用户'}</div>
                  <div className="text-sm text-gray-600 font-mono font-semibold truncate">{user.userUuid}</div>
                </div>
              </div>
              
              {user.userUuid !== userUuid && (
                <button 
                  onClick={() => handleSwitchClick(user.userUuid)}
                  className="px-6 py-2 bg-gray-50 hover:bg-[#ff5c5c] hover:text-white text-gray-600 rounded-xl font-bold text-sm transition-all ml-4 flex-shrink-0"
                >
                  切换
                </button>
              )}
              {user.userUuid === userUuid && (
                <div className="px-4 py-2 bg-[#ff5c5c] text-white rounded-xl font-bold text-sm ml-4 flex-shrink-0">
                  当前
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 添加新账号 */}
      <div className="mb-10">
        <h3 className="text-xl font-black text-gray-800 mb-6 px-2">添加账号</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => {
              setAuthDialogMode('login');
              setShowAuthDialog(true);
            }}
            className="bg-white p-5 rounded-[28px] border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all flex flex-col items-center gap-3 group"
          >
            <div className="w-12 h-12 rounded-2xl bg-pink-50 group-hover:bg-pink-100 flex items-center justify-center transition-colors">
              <User size={24} className="text-pink-500" />
            </div>
            <span className="font-bold text-gray-700">登录已有账号</span>
            <span className="text-xs text-gray-400">用户名/手机号+密码</span>
          </button>
          
          <button 
            onClick={() => {
              setAuthDialogMode('sms-login');
              setShowAuthDialog(true);
            }}
            className="bg-white p-5 rounded-[28px] border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all flex flex-col items-center gap-3 group"
          >
            <div className="w-12 h-12 rounded-2xl bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
              <Phone size={24} className="text-blue-500" />
            </div>
            <span className="font-bold text-gray-700">短信快捷登录</span>
            <span className="text-xs text-gray-400">手机号+验证码</span>
          </button>
          
          <button 
            onClick={() => {
              setAuthDialogMode('add-existing');
              setShowAuthDialog(true);
            }}
            className="bg-white p-5 rounded-[28px] border border-gray-100 hover:border-green-200 hover:shadow-lg transition-all flex flex-col items-center gap-3 group"
          >
            <div className="w-12 h-12 rounded-2xl bg-green-50 group-hover:bg-green-100 flex items-center justify-center transition-colors">
              <Key size={24} className="text-green-500" />
            </div>
            <span className="font-bold text-gray-700">输入UUID添加</span>
            <span className="text-xs text-gray-400">已知账号UUID</span>
          </button>
          
          <button 
            onClick={handleCreateNew}
            className="bg-white p-5 rounded-[28px] border-2 border-dashed border-pink-100 hover:border-[#ff5c5c]/30 transition-all flex flex-col items-center gap-3 group"
          >
            <div className="w-12 h-12 rounded-2xl bg-pink-50 group-hover:bg-[#ff5c5c] flex items-center justify-center transition-colors">
              <Plus size={24} className="text-pink-300 group-hover:text-white" />
            </div>
            <span className="font-bold text-pink-300 group-hover:text-[#ff5c5c]">创建新账号</span>
            <span className="text-xs text-gray-400">生成新的UUID</span>
          </button>
        </div>
      </div>

      {/* 提示信息 */}
      <div className="bg-blue-50 border border-blue-200 rounded-[28px] p-6">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-bold mb-2">💡 使用提示：</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>每个账号的数据会分开保存，切换账号不会丢失数据</li>
              <li>建议绑定手机号，方便找回账号和重置密码</li>
              <li>设置密码后可以使用用户名+密码登录</li>
              <li>一个手机号目前只能绑定一个账号</li>
              <li>请妥善保管用户UUID，它是恢复数据的重要凭证</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 弹窗 */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="切换账号"
        message="确定要切换到以下账号吗？切换后将加载该账号的所有数据。"
        targetUser={pendingSwitchUser}
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
        isLoading={isSwitching}
      />

      <AuthDialog
        isOpen={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        onSuccess={handleAuthSuccess}
        initialMode={authDialogMode}
      />

      {authToken && (
        <>
          <BindPhoneDialog
            isOpen={showBindPhoneDialog}
            onClose={() => setShowBindPhoneDialog(false)}
            onSuccess={() => syncUserInfo()}
            token={authToken}
          />

          <PasswordDialog
            isOpen={showPasswordDialog}
            onClose={() => setShowPasswordDialog(false)}
            onSuccess={() => syncUserInfo()}
            token={authToken}
            hasPassword={serverUserInfo?.hasPassword || false}
          />
        </>
      )}
    </div>
  );
};
